import React, { useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import cx from 'classnames';
import { Row, Col } from 'react-bootstrap'; // Import Row for horizontal layout
import { Core as ReviewButton } from '../ReviewButton';
import { TRACK_CLICK_REVIEW_US_CLICK } from '../../../../src/web/utils/dataLayer';
import useCurrentPage from '../../../hooks/useCurrentPage';
import StarRack from '../../../components/stateless/StarRack';
import Review from '../../../components/stateful/Review';
import Translation from '../../../components/stateless/Translation';
import Button from '../../../../modules/react/components/stateless/Button';
import ReviewsModal from '../../../../src/web/components/ReviewsModal/ReviewsModal';
import styles from './styles.scss';

const propTypes = {
  facility: PropTypes.shape({
    reviews: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      rating: PropTypes.number.isRequired,
      name: PropTypes.string,
      body: PropTypes.string,
      timestamp: PropTypes.string.isRequired,
    })).isRequired,
  }),
  facilityId: PropTypes.string.isRequired,
  showModal: PropTypes.bool.isRequired,
  handleShowModal: PropTypes.func.isRequired,
  handleHideModal: PropTypes.func.isRequired,
  sortOrder: PropTypes.string,
  handleSortOrderChange: PropTypes.func.isRequired,
};

const defaultProps = {
  facility: undefined,
  sortOrder: undefined,
};

function renderReview(review) {
  return (
    <Col xs={12} sm={4} key={review.id}>
      <Review
        author={review.name}
        rating={review.rating}
        body={review.body}
        date={review.timestamp}
      />
    </Col>
  );
}

function renderHighlightedReviews(reviews) {
  return (
    <Row>  {/* Use Row for horizontal layout */}
      {reviews
        .sort((a, b) => b.timestamp.localeCompare(a.timestamp))
        .slice(0, 3)
        .map(renderReview)}
    </Row>
  );
}

function renderLeaveReviewBlock(facilityId, reviews) {
  if (reviews.length >= 3) {
    return null;
  }

  const { primaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    buttonStyleClass } = useCurrentPage();
  const displayAmountClass = reviews.length ? null : styles.solo;

  return (
    <Col
      xs={12}
      sm={(3 - reviews.length) * 4}
      className={cx(
        styles.callToAction,
        displayAmountClass,
      )}
    >
      <ReviewButton
        className={cx(
          primaryColorBackgroundClass,
          primaryColorBackgroundHoverClass,
          buttonStyleClass,
          styles.reviewButton,
        )}
        facilityId={facilityId}
        {...TRACK_CLICK_REVIEW_US_CLICK}
      >
        <StarRack rating={0} showUnfilled className={styles.stars} />
        <Translation
          id="widgets.internalReviews.writeReviewButton"
          defaultMessage="Enjoy storing with us? Tell us about it!"
        >
          {text => (
            <div style={{ whiteSpace: 'initial' }}>{text}</div>
          )}
        </Translation>
      </ReviewButton>
    </Col>
  );
}

const FacilityReviews = ({
  facility,
  facilityId,
  showModal,
  handleShowModal,
  handleHideModal,
  sortOrder,
  handleSortOrderChange,
}) => {
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);

  if (!facility) {
    return null;
  }

  const { primaryColorBackgroundClass,
    primaryColorBackgroundHoverClass,
    buttonStyleClass } = useCurrentPage();

  const handleViewMoreReviews = () => {
    handleShowModal();
    setSelectedFacilityId(facility.id);
  };

  const handleReviewModalHide = () => {
    handleHideModal();
    setSelectedFacilityId(null);
  };

  return (
    <div className={facility.reviews.length < 3 ? styles.makeTable : null}>
      {renderHighlightedReviews(facility.reviews)}
      {renderLeaveReviewBlock(facilityId, facility.reviews)}
      {facility.reviews.length > 3 && (
        <Col sm={12} mdOffset={4} md={4}>
          <Button
            block
            variant="primary"
            className={cx(
              primaryColorBackgroundClass,
              primaryColorBackgroundHoverClass,
              buttonStyleClass,
            )}
            onClick={handleViewMoreReviews}
          >
            <Translation id="widgets.internalReviews.viewMoreButton" defaultMessage="View More Reviews">
              {text => text}
            </Translation>
          </Button>
          <ReviewsModal
            facility={facility}
            onClose={handleReviewModalHide}
            sortOrder={sortOrder}
            onOrderChange={handleSortOrderChange}
            show={showModal && (facility.id === selectedFacilityId)}
          />
        </Col>
      )}
    </div>
  );
};

FacilityReviews.propTypes = propTypes;
FacilityReviews.defaultProps = defaultProps;

FacilityReviews.fragments = {
  facility: gql`
    fragment FacilityReviewsFacility on Facility {
      ...ReviewsModalFacility
    }

    ${ReviewsModal.fragments.facility}
  `,
};

export default FacilityReviews;
